import {
	Component, Input, Output, EventEmitter, SimpleChanges, OnDestroy,
	OnChanges
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ProtocolSettings, ProtocolSettingsTypes, PrtgSettings } from 'DiscoveryEngine.TS/Protocols';
import { Guid, StringUtils } from "DiscoveryEngine.TS/Utils";
import { ComponentBase, Changeable } from "common";
import { NavigationService } from 'netview';
import { DiscResultsService } from 'discovery';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { IdentityService, LicensingService } from 'identity';
import { MessageService } from 'base';
import { ToolsService } from 'tools';
import { HuduExport } from 'DiscoveryEngine.TS/Export/HuduExport';
import { ConfigurationService } from '../../../configuration';
import { AgentDto } from '../../../UvExplorerServer.Dtos.TS/Agents';
import { ScheduledDiscovery } from '../../../DiscoveryEngine.TS/Discovery';


@Component({
	selector: 'hudu-export-config',
	templateUrl: '../hudu-export-config/hudu-export-config.component.html',
	styleUrls: ['../hudu-export-config/hudu-export-config.component.scss']
})
export class HuduExportConfigComponent extends ComponentBase implements OnDestroy, OnChanges, Changeable {

	@Input()
	public Network: NetViewDto;

	@Input()
	public HuduExport: HuduExport;

	@Input()
	public Agent: AgentDto;

	@Input()
	public ScheduledDiscovery: ScheduledDiscovery;

	@Input()
	public EnableExportOrigin: boolean;

	public SettingsForm: UntypedFormGroup;

	public EnableExport: UntypedFormControl;

	public TargetServerApiURL: UntypedFormControl;
	public TargetServerApiKey: UntypedFormControl;
	public CompanyName: UntypedFormControl;
	public NetworkName: UntypedFormControl;

	public ExportOrigin: UntypedFormControl;

	public ExportDevices: UntypedFormControl;
	public ExportIpAddresses: UntypedFormControl;
	public ExportConfigs: UntypedFormControl;
	public ExportPasswords: UntypedFormControl;
	public ExportMapToLocation: UntypedFormControl;

	public MapFileName: UntypedFormControl;
	public MapFileExtension: UntypedFormControl;

	private apiKeyChanged: boolean;
	private apiKeyChangedSub: Subscription;

	public mapDropdownOptions: string[];

	constructor(private fb: UntypedFormBuilder,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private messageService: MessageService,
		private configService: ConfigurationService,
		private toolsService: ToolsService) {

		super();

		this.Network = null;
		this.HuduExport = null;
		this.EnableExportOrigin = false;

		this.mapDropdownOptions = HuduExport.mapFormatDropdownOptions;
	}

	public ngOnDestroy(): void {
		if (this.apiKeyChangedSub) {
			this.apiKeyChangedSub.unsubscribe();
		}
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	protected BuildForm(): void {

		let enableExport = false;

		// Hudu settings
		let serverURL = "";
		let serverApiKey = "";
		let companyName = "";
		let networkName = "";

		let exportOrigin = "";

		let exportDevices = false;
		let exportIpAddresses = false;
		let exportConfigs = false;
		let exportPassswords = false;
		let exportMapToLocation = false;

		let mapFilename = "";
		let mapFileExt = "";

		if (this.HuduExport) {
			let value = this.HuduExport;

			enableExport = value.EnableExport;

			if (value.HasTargetServerApiURL)
				serverURL = value.TargetServerApiURL;
			if (value.HasTargetServerApiKey)
				serverApiKey = this.hidePassword(value.TargetServerApiKey);
			if (value.HasCompanyName)
				companyName = value.CompanyName;
			if (value.HasNetworkName)
				networkName = value.NetworkName;

			if (value.HasExportOrigin)
				exportOrigin = value.ExportOrigin;

			exportDevices = value.ExportDevices;
			exportIpAddresses = value.ExportIpAddresses;
			exportConfigs = value.ExportConfigs;
			exportPassswords = value.ExportPasswords;
			exportMapToLocation = value.ExportMapToNetwork;

			mapFilename = value.MapFilename;
			mapFileExt = value.MapFileExtension;
		}

		this.EnableExport = new UntypedFormControl(enableExport);

		this.TargetServerApiURL = new UntypedFormControl(serverURL);
		this.TargetServerApiKey = new UntypedFormControl(serverApiKey);
		this.CompanyName = new UntypedFormControl(companyName);
		this.NetworkName = new UntypedFormControl(networkName);

		this.ExportOrigin = new UntypedFormControl(exportOrigin);

		this.ExportDevices = new UntypedFormControl(exportDevices);
		this.ExportIpAddresses = new UntypedFormControl(exportIpAddresses);
		this.ExportConfigs = new UntypedFormControl(exportConfigs);
		this.ExportPasswords = new UntypedFormControl(exportPassswords);
		this.ExportMapToLocation = new UntypedFormControl(exportMapToLocation);

		this.MapFileName = new UntypedFormControl(mapFilename);
		this.MapFileExtension = new UntypedFormControl(mapFileExt);

		this.apiKeyChanged = false;
		this.apiKeyChangedSub = this.TargetServerApiKey.valueChanges.subscribe(() => {
			this.apiKeyChanged = true;
		});

		this.SettingsForm = this.fb.group({
			EnableExport: this.EnableExport,
			TargetServerApiURL: this.TargetServerApiURL,
			TargetServerApiKey: this.TargetServerApiKey,
			CompanyName: this.CompanyName,
			NetworkName: this.NetworkName,
			ExportOrigin: this.ExportOrigin,
			ExportDevices: this.ExportDevices,
			ExportIpAddresses: this.ExportIpAddresses,
			ExportConfigs: this.ExportConfigs,
			ExportPasswords: this.ExportPasswords,
			ExportMapToLocation: this.ExportMapToLocation,
			MapFileName: this.MapFileName,
			MapFileExtension: this.MapFileExtension,
		},
		{
			validator: Validators.compose([
				this.validHuduSettings(this.EnableExport, this.TargetServerApiURL, this.TargetServerApiKey, this.CompanyName)

				// Group Name is now optional
				//this.validGroupName(this.EnableExport, this.GroupName)
			])
		});
	}

	public get ExportEnabled(): boolean {
		if (this.EnableExport) {
			return this.EnableExport.value;
		}
		else {
			return false;
		}
	}

	public ngOnInit(): void {
		this.BuildForm();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.BuildForm();
	}

	public get tempChanges(): boolean {
		return this.SettingsForm.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.SettingsForm.valid;
	}

	public discardTempChanges(): void {
		this.BuildForm();
	}

	public applyTempChanges(): void {
		if (this.HuduExport) {
			let value = this.HuduExport;

			value.EnableExport = this.EnableExport.value;

			value.TargetServerApiURL = this.TargetServerApiURL.value;
			if (this.apiKeyChanged) {
				value.TargetServerApiKey = StringUtils.ObfuscateString2(this.TargetServerApiKey.value);
			}
			value.CompanyName = this.CompanyName.value;
			value.NetworkName = this.NetworkName.value;

			value.ExportOrigin = this.ExportOrigin.value;

			value.ExportDevices = this.ExportDevices.value;
			value.ExportIpAddresses = this.ExportIpAddresses.value;
			value.ExportConfigs = this.ExportConfigs.value;
			value.ExportPasswords = this.ExportPasswords.value;
			value.ExportMapToNetwork = this.ExportMapToLocation.value;

			value.MapFilename = this.MapFileName.value;
			value.MapFileExtension = this.MapFileExtension.value;
			
			this.BuildForm();
		}
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.apiKeyChanged) {
			this.apiKeyChanged = true;

			this.TargetServerApiKey.setValue('');
			this.TargetServerApiKey.markAsDirty();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["Hudu Export"];
	}

	private validHuduSettings(enableExport: UntypedFormControl, serverApiURL: UntypedFormControl, serverApiKey: UntypedFormControl, organization: UntypedFormControl): ValidatorFn {
		return () => {
			if (enableExport.value) {
				if (!serverApiURL.value) {
					return {
						validHuduSettings: { valid: false }
					};
				}
				else if (!serverApiKey.value) {
					return {
						validHuduSettings: { valid: false }
					};
				}
				else if (!organization.value) {
					return {
						validHuduSettings: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}


	private validCompany(enableExport: UntypedFormControl, company: UntypedFormControl): ValidatorFn {
		return () => {
			if (enableExport.value) {
				if (!company.value) {
					return {
						validCompany: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}

	public ExportNow(): void {
		if (this.tempChanges) {
			this.messageService.displayInfoMessage(
				"You have unsaved changes to your Hudu export settings.\nPlease save before exporting.", 10000);
		}
		else {
			if (!!this.Network && !!this.Network.Guid) {
				let sessionGuid = this.identityService.sessionGuid;
				let netViewGuid = this.Network.Guid;
				let agentGuid = this.navService.location.agentGuid;
				let schedDiscGuid = this.navService.location.schedDiscGuid;
				this.discResultsService.runHuduExport(sessionGuid, netViewGuid, agentGuid, schedDiscGuid).subscribe(
					(result: boolean) => {
						this.messageService.displayInfoMessage("Hudu Export has been initiated");
					},
					(error) => {
						this.messageService.displayErrorMessage("Hudu Export failed");
					});
			}
		}
	}

	public get enableTestHuduButton(): boolean {
		return (!!this.TargetServerApiURL && !!this.TargetServerApiURL.value
			&& !!this.TargetServerApiKey && !!this.TargetServerApiKey.value);
	}

	public testHuduConnect(): void {
		let apiKey = "";
		if (this.apiKeyChanged) {
			apiKey = StringUtils.ObfuscateString2(this.TargetServerApiKey.value);
		}
		else {
			apiKey = this.HuduExport.TargetServerApiKey;
		}
		this.configService.testHuduConnection(this.TargetServerApiURL.value, apiKey).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully connected to Hudu");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to connect to Hudu");
			});
	}
}
